import { type PriceCalculationUnit } from "../api";

/**
 * Represents a parsed unit with separate numerators and denominators
 */
export interface ParsedUnit {
    nums: string[];
    denoms: string[];
}

/**
 * Cancels matching terms between numerators and denominators
 * Maintains sorted order since inputs are sorted
 */
export const cancelMatchingTerms = (parsed: ParsedUnit): ParsedUnit => {
    const nums = [...parsed.nums];
    const denoms = [...parsed.denoms];

    // Cancel matching terms (arrays remain sorted after removal)
    for (let i = nums.length - 1; i >= 0; i--) {
        const num = nums[i];
        if (num === undefined) {
            continue;
        }
        const idx = denoms.indexOf(num);
        if (idx !== -1) {
            nums.splice(i, 1);
            denoms.splice(idx, 1);
        }
    }

    return { nums, denoms };
};

/**
 * Parses a unit string into its numerator and denominator components
 * N1/D1/N2/D2 → nums: [N1, N2], denoms: [D1, D2]
 * Arrays are sorted and reduced (matching terms cancelled) for efficient comparison
 */
export const parseUnitComponents = (unit: PriceCalculationUnit): ParsedUnit => {
    const parts = unit.split("/");
    const nums: string[] = [];
    const denoms: string[] = [];

    parts.forEach((part, i) => {
        if (part === "scalar") {
            return; // skip scalar
        }
        if (i % 2 === 0) {
            nums.push(part); // even positions = numerators
        } else {
            denoms.push(part); // odd positions = denominators
        }
    });

    // Sort and cancel matching terms (e.g., m²/m² → scalar)
    return cancelMatchingTerms({ nums: nums.sort(), denoms: denoms.sort() });
};

/**
 * Multiplies two parsed units
 * Maintains sorted order by sorting after combining
 */
export const multiplyParsedUnits = (u1: ParsedUnit, u2: ParsedUnit): ParsedUnit => {
    const allNums = [...u1.nums, ...u2.nums].sort();
    const allDenoms = [...u1.denoms, ...u2.denoms].sort();

    return cancelMatchingTerms({ nums: allNums, denoms: allDenoms });
};

/**
 * Inverts a parsed unit by swapping numerators and denominators
 * A/B → B/A
 */
export const invertParsedUnit = (parsed: ParsedUnit): ParsedUnit => {
    return { nums: parsed.denoms, denoms: parsed.nums };
};

/**
 * Checks if two parsed units are equivalent by comparing numerators and denominators
 * Assumes units are already sorted from parseUnitComponents
 */
export const parsedUnitsAreEquivalent = (u1: ParsedUnit, u2: ParsedUnit): boolean => {
    return u1.nums.join(",") === u2.nums.join(",") && u1.denoms.join(",") === u2.denoms.join(",");
};

/**
 * Builds a unit string from a parsed unit by interleaving numerators and denominators
 * [N1, N2] + [D1, D2] → N1/D1/N2/D2
 */
export const buildUnitString = (parsed: ParsedUnit): PriceCalculationUnit => {
    if (parsed.nums.length === 0 && parsed.denoms.length === 0) {
        return "scalar";
    }

    const result: string[] = [];
    const maxLen = Math.max(parsed.nums.length, parsed.denoms.length);

    for (let i = 0; i < maxLen; i++) {
        // Push numerator (or "scalar" if missing and more denoms follow)
        const num = parsed.nums[i];
        if (num !== undefined) {
            result.push(num);
        } else if (i < parsed.denoms.length) {
            // Need a numerator placeholder because denom follows
            result.push("scalar");
        }

        // Push denominator (or "scalar" if missing and more nums follow)
        const denom = parsed.denoms[i];
        if (denom !== undefined) {
            result.push(denom);
        } else if (i + 1 < parsed.nums.length) {
            // Need a denominator placeholder because more nums follow
            result.push("scalar");
        }
    }

    return result.join("/") as PriceCalculationUnit;
};

/**
 * Normalizes a unit string by parsing and rebuilding it.
 * This ensures consistent representation (e.g., "m/s" and "m/s" are the same).
 */
export const normalizeUnit = (unit: PriceCalculationUnit): PriceCalculationUnit => {
    const parsed = parseUnitComponents(unit);
    return buildUnitString(parsed);
};

/**
 * Calculates the gap unit needed to reach target from current position
 * Given: current * X = target
 * Returns: X (the gap)
 * Maintains sorted order by sorting after combining
 */
export const calculateGap = (currentParsed: ParsedUnit, targetParsed: ParsedUnit): ParsedUnit => {
    // To find what X satisfies: current * X = target
    // We conceptually divide: X = target / current
    // This means:
    //   - Gap numerators = target nums + current denoms (to provide target and cancel current's denoms)
    //   - Gap denominators = target denoms + current nums (to cancel what current provides)

    const gapNums = [...targetParsed.nums, ...currentParsed.denoms].sort();
    const gapDenoms = [...targetParsed.denoms, ...currentParsed.nums].sort();

    // Cancel matching terms to simplify
    return cancelMatchingTerms({ nums: gapNums, denoms: gapDenoms });
};

/**
 * Checks if a candidate unit has components that could help fill the gap
 * Since gap is calculated as target/current, it directly represents what we need to multiply by.
 * We just need to check if candidate matches the gap structure.
 */
export const hasMatchingComponents = (candidateParsed: ParsedUnit, gapParsed: ParsedUnit): boolean => {
    // Check numerators: candidate.nums ∩ gap.nums (provides what's needed in numerator)
    const numsMatch = candidateParsed.nums.some(n => gapParsed.nums.includes(n));

    // Check denominators: candidate.denoms ∩ gap.denoms (provides what's needed in denominator)
    const denomsMatch = candidateParsed.denoms.some(d => gapParsed.denoms.includes(d));

    return numsMatch || denomsMatch;
};

/**
 * Recursively finds all unit chains that multiply to the target unit.
 * Works at the unit level (not parameter level) for efficiency.
 * When a unit is used, its inverse is also removed from remaining units
 * to prevent unit × INV(unit) = scalar chains.
 */
export const findUnitCombinationsRecursive = (
    targetParsed: ParsedUnit,
    currentParsed: ParsedUnit,
    currentChain: PriceCalculationUnit[],
    remainingUnits: PriceCalculationUnit[],
    parsedUnitsMap: Map<PriceCalculationUnit, ParsedUnit>
): PriceCalculationUnit[][] => {
    // Base case: reached target
    if (parsedUnitsAreEquivalent(currentParsed, targetParsed)) {
        return [currentChain];
    }

    // Calculate what we still need (using parsed units)
    const gapParsed = calculateGap(currentParsed, targetParsed);

    // Find UNITS that share components with the gap (using parsed units)
    const matchingUnits = remainingUnits.filter(unit => {
        const unitParsed = parsedUnitsMap.get(unit)!;
        return hasMatchingComponents(unitParsed, gapParsed);
    });

    // If no matching units, this path cannot reach the target
    if (matchingUnits.length === 0) {
        return [];
    }

    const results: PriceCalculationUnit[][] = [];
    for (const unit of matchingUnits) {
        // Multiply to get new accumulated unit
        const unitParsed = parsedUnitsMap.get(unit)!;
        const newParsed = multiplyParsedUnits(currentParsed, unitParsed);

        // Remove ALL occurrences of both the used unit AND its inverse
        // to prevent complex chains that "undo" each other
        const inverseUnit = buildUnitString(invertParsedUnit(unitParsed));
        const newRemainingUnits = remainingUnits.filter(u => u !== unit && u !== inverseUnit);

        // Recursively find remaining chain
        const chains = findUnitCombinationsRecursive(
            targetParsed,
            newParsed,
            [...currentChain, unit],
            newRemainingUnits,
            parsedUnitsMap
        );

        results.push(...chains);
    }

    return results;
};
